import { useState, useCallback, useEffect, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Fade from "embla-carousel-fade";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { optimizeImage, optimizeImageSrcSet } from '@/lib/imageOptimization';
import { useIsMobile } from "@/hooks/use-mobile";
import { useInView } from 'react-intersection-observer';
import { trackEvent, ANALYTICS_EVENTS } from '@/lib/analytics';

interface GalleryImage {
  src: string;
  alt?: string;
}

interface ImageGalleryRendererProps {
  images: GalleryImage[];
  className?: string;
}

/**
 * Translate desktop layout class to mobile layout class:
 * - Breakout (media-default) → Full width (media-full-width)
 * - Breakout cropped (media-default-cropped) → Banner (media-full-width-cropped)
 * - Full width, Banner, Inline → unchanged
 */
function getMobileLayoutClass(desktopClass: string): string {
  if (desktopClass.includes("media-default-cropped")) {
    return desktopClass.replace("media-default-cropped", "media-full-width-cropped");
  }
  if (desktopClass.includes("media-default")) {
    return desktopClass.replace("media-default", "media-full-width");
  }
  return desktopClass;
}

export default function ImageGalleryRenderer({
  images,
  className,
}: ImageGalleryRendererProps) {
  const isMobile = useIsMobile();
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "center" },
    [Fade()]
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  // ⚠️ ANALYTICS: Gallery tracking state
  const hasTrackedView = useRef(false);
  const viewedImages = useRef<Set<number>>(new Set([0])); // First image viewed by default
  
  // Track first image dimensions to use as anchor for all others
  const [anchorDimensions, setAnchorDimensions] = useState<{ width: number; height: number } | null>(null);

  // ⚠️ ANALYTICS: Track visibility for gallery view event
  const { ref: visibilityRef, inView: isGalleryVisible } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const newIndex = emblaApi.selectedScrollSnap();

    // ⚠️ ANALYTICS: Track gallery navigation
    if (newIndex !== selectedIndex) {
      viewedImages.current.add(newIndex);

      trackEvent(ANALYTICS_EVENTS.GALLERY_SCROLLED, {
        gallery_type: 'article_inline',
        from_index: selectedIndex,
        to_index: newIndex,
        direction: newIndex > selectedIndex ? 'next' : 'prev',
        total_images: images.length,
        unique_images_viewed: viewedImages.current.size,
        article_url: window.location.pathname,
      });
    }

    setSelectedIndex(newIndex);
  }, [emblaApi, selectedIndex, images.length]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Load first image to get anchor dimensions
  useEffect(() => {
    if (!images || images.length === 0) return;
    
    const img = new Image();
    img.onload = () => {
      setAnchorDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.src = optimizeImage(images[0].src, { width: 1200 });
  }, [images]);

  // ⚠️ ANALYTICS: Track gallery view when visible
  useEffect(() => {
    if (isGalleryVisible && images && images.length > 0 && !hasTrackedView.current) {
      hasTrackedView.current = true;
      trackEvent(ANALYTICS_EVENTS.GALLERY_VIEWED, {
        gallery_type: 'article_inline',
        total_images: images.length,
        article_url: window.location.pathname,
        is_full_width: className?.includes('full-width') || false,
      });
    }
  }, [isGalleryVisible, images, className]);

  if (!images || images.length === 0) return null;

  // Apply mobile layout translation
  const effectiveClassName = isMobile ? getMobileLayoutClass(className || "") : className;

  const isCropped = effectiveClassName?.includes("-cropped");
  const isFullWidth = effectiveClassName?.includes("full-width");
  const radiusClass = isFullWidth ? "rounded-none" : "rounded-3xl";

  // Mobile cropped height is shorter
  const croppedHeight = isMobile ? "280px" : "420px";

  return (
    <figure
      ref={visibilityRef}
      className={cn(
        "image-gallery relative my-8",
        effectiveClassName
      )}
    >
      {/* Gallery Container with overlay arrows */}
      <div className="relative">
        {/* Carousel */}
        <div 
          ref={emblaRef} 
          className={cn("overflow-hidden", radiusClass)}
          style={isCropped ? { height: croppedHeight } : undefined}
        >
          <div className="flex h-full">
            {images.map((image, index) => (
              <div
                key={index}
                className="flex-[0_0_100%] min-w-0 relative"
                style={isCropped ? {
                  opacity: selectedIndex === index ? 1 : 0, 
                  transition: "opacity 0.3s ease",
                  height: "100%",
                } : { 
                  opacity: selectedIndex === index ? 1 : 0, 
                  transition: "opacity 0.3s ease",
                  // Use anchor aspect ratio if available
                  paddingBottom: anchorDimensions 
                    ? `${(anchorDimensions.height / anchorDimensions.width) * 100}%` 
                    : "56.25%", // fallback to 16:9
                }}
              >
                <img
                  src={optimizeImage(image.src, { width: 1200 })}
                  srcSet={optimizeImageSrcSet(image.src, [600, 900, 1200, 1800])}
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px"
                  alt={image.alt || `Gallery image ${index + 1}`}
                  loading="lazy"
                  decoding="async"
                  width={1200}
                  height={675}
                  className={cn("absolute inset-0 w-full h-full object-cover object-center", radiusClass)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Left Arrow - positioned inside */}
        {images.length > 1 && (
          <button
            onClick={scrollPrev}
            className={cn(
              "absolute left-3 top-1/2 -translate-y-1/2 z-10",
              "flex h-10 w-10 items-center justify-center rounded-full",
              "bg-background/80 backdrop-blur-sm text-foreground",
              "transition-all duration-200",
              "hover:bg-background hover:scale-105",
              "focus:outline-none focus:ring-2 focus:ring-primary"
            )}
            aria-label="Previous image"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}

        {/* Right Arrow - positioned inside */}
        {images.length > 1 && (
          <button
            onClick={scrollNext}
            className={cn(
              "absolute right-3 top-1/2 -translate-y-1/2 z-10",
              "flex h-10 w-10 items-center justify-center rounded-full",
              "bg-background/80 backdrop-blur-sm text-foreground",
              "transition-all duration-200",
              "hover:bg-background hover:scale-105",
              "focus:outline-none focus:ring-2 focus:ring-primary"
            )}
            aria-label="Next image"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Pagination Dots */}
      {images.length > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollTo(index)}
              className={cn(
                "w-2 h-2 rounded-full transition-all duration-200",
                selectedIndex === index
                  ? "bg-foreground"
                  : "bg-foreground/30 hover:bg-foreground/50"
              )}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </figure>
  );
}
